import { useDashboardBreadcrumb } from '@crehana/compass.web/page-layout';

type TUsePageHeaderTitleArgs = {
  /** Static i18n string — used only until the breadcrumb trail resolves, or if it never matches. */
  staticTitle: string;
  /** Forwarded verbatim to `useDashboardBreadcrumb` — must match the `homeBreadcrumb` prop passed to `DashboardPageLayout` so both resolve the same trail. */
  homeBreadcrumb?: boolean;
};

/**
 * CORE-1691 fix — `DashboardPageLayout`'s header title and its breadcrumb leaf can diverge:
 * the header renders whatever `title` string the page passes, while the breadcrumb leaf is
 * BE-driven (`organization_settings.get_sidebar` via `useDashboardBreadcrumb`'s sidebar-tree
 * inference). Whenever the BE label differs from the hardcoded i18n string (a numeric
 * test-org suffix, different wording, etc.) the header and breadcrumb show two different
 * labels for the same page.
 *
 * This hook resolves the SAME trail `DashboardPageLayout` will render internally and returns
 * its leaf label, so callers can pass that (instead of the static string) as `title` — making
 * the header always match the breadcrumb leaf. Falls back to `staticTitle` while the trail is
 * still loading or when it has no items (sidebar API error / no URL match), so the header never
 * flashes empty.
 */
export const usePageHeaderTitle = ({
  staticTitle,
  homeBreadcrumb,
}: TUsePageHeaderTitleArgs): string => {
  const { title: leafTitle, isLoading } = useDashboardBreadcrumb({
    homeBreadcrumb,
  });

  return !isLoading && leafTitle ? leafTitle : staticTitle;
};
